import { useState, useMemo, useEffect } from 'react'
import { syncFromSupabase } from '../utils/supabase'
import { RefreshCw } from 'lucide-react'
import { GEOMETRIA, calcularCusto } from '../data/geometria'
import { CORES, GRUPOS_CORES } from '../data/cores'
import { ADICIONAIS_BOOLEAN, ADICIONAIS_QUANTIDADE, ADICIONAIS_DEFAULT, PUXADOR_TIPOS, PUXADOR_CORES } from '../data/adicionais'
import { PRECO_ADICIONAL, PRECO_PUXADOR } from '../data/precosAdicionais'
import { getPrecos } from '../utils/precos'
import { Wrench, Check, RotateCcw, Calculator, Copy } from 'lucide-react'
import { OrcamentoPersonalizado } from '../components/OrcamentoPersonalizado'
import { ModeloCombobox } from '../components/form/ModeloCombobox'
import { fmt, DESCRICAO_COMERCIAL } from '../utils/fmt'

// Arredonda para o próximo preço terminado em X9,90
function arredondarPreco(price) {
  if (!price || price <= 0) return 0
  const cents = Math.round(price * 100)
  const base  = Math.floor(cents / 1000) * 1000 + 990
  return (base >= cents ? base : base + 1000) / 100
}
// n_* são inteiros com padrão; dimensões físicas ficam em string vazia até o user digitar
const DEFAULT_DIMS = { C: '', P: '', A: '', C1: '', C2: '', n_g: 4, n_prat: 4, n_div: 5, n_portas: 2 }
// Campos que são inteiros (não convertidos de cm → m)
const INT_FIELDS = new Set(['n_g','n_prat','n_div','n_portas'])

// Junta itens em prosa: ['a','b','c'] → 'a, b e c'
function listarProsa(items) {
  if (!items || items.length === 0) return ''
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} e ${items[1]}`
  return items.slice(0, -1).join(', ') + ' e ' + items[items.length - 1]
}

// Descrição em prosa do que o móvel tem (gavetas, portas, prateleiras, etc.)
// Combina inferências da geometria + adicionais escolhidos.
function descricaoProsa(geo, adicionais) {
  if (!geo) return []
  const partes = []
  const pecas  = geo.pecas || []
  const conta  = (regex) => pecas
    .filter(p => regex.test(p.nome))
    .reduce((s, p) => s + (typeof p.qtd === 'number' ? p.qtd : 0), 0)

  const nGav        = conta(/^Frente gaveta/i)
  const nPortasGiro = conta(/^Porta de giro/i)
  const nPortasBasc = conta(/^Porta basculante/i)
  const nPrat       = conta(/^Prateleira/i)
  const ferr        = (typeof geo.ferragens === 'object' && geo.ferragens) || {}

  if (nGav > 0)        partes.push(`${nGav} ${nGav === 1 ? 'gaveta' : 'gavetas'}`)
  if (nPortasGiro > 0) partes.push(`${nPortasGiro} ${nPortasGiro === 1 ? 'porta de giro' : 'portas de giro'}`)
  if (nPortasBasc > 0) partes.push(`${nPortasBasc} ${nPortasBasc === 1 ? 'porta basculante' : 'portas basculantes'}`)
  if (nPrat > 0)       partes.push(`${nPrat} ${nPrat === 1 ? 'prateleira' : 'prateleiras'}`)
  if (Number(ferr.cabide) > 0)  partes.push(`cabideiro`)

  // Adicionais escolhidos pela vendedora
  if (adicionais.puxadorTipo) {
    const c = adicionais.puxadorCor ? ` ${adicionais.puxadorCor.toLowerCase()}` : ''
    const q = adicionais.puxadorQtd && adicionais.puxadorQtd > 1 ? ` (×${adicionais.puxadorQtd})` : ''
    partes.push(`puxador ${adicionais.puxadorTipo.toLowerCase()}${c}${q}`)
  }
  ADICIONAIS_BOOLEAN.forEach(({ key, label }) => {
    if (adicionais[key]) partes.push(label.toLowerCase())
  })
  ADICIONAIS_QUANTIDADE.forEach(({ key, label }) => {
    const qtd = Number(adicionais[key] || 0)
    if (qtd > 0) {
      const labelSemPlural = label.toLowerCase().replace(/s$/, '')
      partes.push(`${qtd} ${qtd === 1 ? labelSemPlural : label.toLowerCase()}`)
    }
  })

  return partes
}

// Monta texto pré-formatado pro WhatsApp e expõe botão de cópia.
// Formato comercial enxuto: ▪Modelo CxPxAcm com X. <preço> na cor Y. Em até 10x.
function TextoWhatsApp({ modelo, corObj, dims, geo, adicionais, preco, copiado, setCopiado }) {
  const texto = useMemo(() => {
    // Medidas em prosa (C×P×A) sem labels
    const ordem = ['C', 'C1', 'C2', 'P', 'A']
    const valoresMed = ordem
      .filter(k => geo?.entradas?.includes(k) && dims[k] && Number(dims[k]) > 0)
      .map(k => dims[k])
    const medidasTxt = valoresMed.length ? ` ${valoresMed.join('x')}cm` : ''

    // Descrição comercial + sufixo. Se o modelo tem entrada em
    // DESCRICAO_COMERCIAL (fmt.js), usa nome comercial + sufixo curto
    // que a vendedora definiu. Senão, cai no nome canônico + prosa
    // gerada a partir da geometria (comportamento antigo).
    const comerc      = DESCRICAO_COMERCIAL[modelo]
    const nomeExibido = comerc?.nome || modelo
    const partes      = descricaoProsa(geo, adicionais)
    // sufixo pode ser string estática OU função(m) → string (armários
    // multiuso com "X prateleiras" onde X depende dos adicionais). Aqui
    // não temos o `m` completo — passamos { adicionais } que é o que a
    // função consulta.
    const sufixoResolvido = typeof comerc?.sufixo === 'function'
      ? comerc.sufixo({ adicionais })
      : comerc?.sufixo
    const detalhesTxt = sufixoResolvido
      ? ` ${sufixoResolvido}`
      : (partes.length ? ` com ${listarProsa(partes)}` : '')

    const linha1 = `▪${nomeExibido}${medidasTxt}${detalhesTxt}.`
    const linha2 = `R$ ${fmt(preco).replace('R$', '').trim()}${corObj ? ` na cor ${corObj.nome}` : ''}`
    const linha3 = 'Em até 10x sem juros'

    return [linha1, '', linha2, '', linha3].join('\n')
  }, [modelo, corObj, dims, geo, adicionais, preco])

  function copiar() {
    if (!navigator.clipboard) return
    navigator.clipboard.writeText(texto).then(() => {
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2000)
    })
  }

  return (
    <div className="mt-3 border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Texto pra WhatsApp</span>
        <button
          type="button"
          onClick={copiar}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-colors
            ${copiado
              ? 'bg-emerald-500 text-white'
              : 'bg-[#25D366] hover:bg-[#1ebe5b] text-white'}`}
        >
          {copiado ? <Check size={12} strokeWidth={3} /> : <Copy size={12} />}
          {copiado ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      <pre className="px-3 py-2 text-[11px] text-slate-700 whitespace-pre-wrap font-sans leading-snug bg-white">
        {texto}
      </pre>
    </div>
  )
}

function Stepper({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => onChange(Math.max(0, value - 1))}
        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm flex items-center justify-center">−</button>
      <span className="w-5 text-center text-sm font-bold text-[#111111]">{value}</span>
      <button type="button" onClick={() => onChange(value + 1)}
        className="w-6 h-6 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm flex items-center justify-center">+</button>
    </div>
  )
}

export function CalculadoraCusto() {
  const [orcamentoOpen, setOrcamentoOpen] = useState(false)
  const [modelo,     setModelo]     = useState('')
  const [dims,       setDims]       = useState({ ...DEFAULT_DIMS })
  const [cor,        setCor]        = useState('')
  const [grupoCor,   setGrupoCor]   = useState('Sólidos')
  const [adicionais, setAdicionais] = useState({ ...ADICIONAIS_DEFAULT })
  const [copiado,    setCopiado]    = useState(false)
  // Contador que força re-leitura dos precos do localStorage quando o
  // useAutoSync acabou de re-sincronizar com Supabase. Sem isso, a
  // vendedora ficava com preços defasados no celular até reabrir o app.
  const [versaoPrecos, setVersaoPrecos] = useState(0)
  const [atualizando,  setAtualizando]  = useState(false)

  // Escuta o evento global disparado quando o app re-sincroniza com
  // Supabase em background (visibilitychange / focus / online).
  useEffect(() => {
    function onResynced() { setVersaoPrecos(v => v + 1) }
    window.addEventListener('mini:resynced', onResynced)
    return () => window.removeEventListener('mini:resynced', onResynced)
  }, [])

  async function atualizarManual() {
    if (atualizando) return
    setAtualizando(true)
    try {
      const ok = await syncFromSupabase()
      if (ok) {
        setVersaoPrecos(v => v + 1)
        window.dispatchEvent(new CustomEvent('mini:resynced', { detail: { motivo: 'manual' } }))
      }
    } finally {
      setAtualizando(false)
    }
  }

  const perda = 0.10
  const geo   = modelo ? GEOMETRIA[modelo] : null

  function setDim(k, v) { setDims(p => ({ ...p, [k]: v })) }
  function setAd(k, v)  { setAdicionais(p => ({ ...p, [k]: v })) }
  // Mantém as medidas ao trocar de modelo — facilita orçar várias variantes
  // com a mesma C×P×A. Botão "Limpar" zera tudo quando necessário.
  function handleModelo(nome) { setModelo(nome) }

  function resetar() {
    setModelo(''); setDims({ ...DEFAULT_DIMS })
    setCor(''); setGrupoCor('Sólidos')
    setAdicionais({ ...ADICIONAIS_DEFAULT })
  }

  const corObj  = CORES.find(c => c.nome === cor)
  const tipoCor = corObj
    ? (corObj.tipo === 'Lacas' ? 'laca' : corObj.categoria === 'branco' ? 'branco' : 'cor')
    : 'branco'

  const vars = useMemo(() => {
    if (!geo) return {}
    const v = {}
    for (const k of geo.entradas) {
      if (INT_FIELDS.has(k)) { v[k] = Number(dims[k]) || DEFAULT_DIMS[k] || 1; continue }
      v[k] = dims[k] ? Number(dims[k]) / 100 : 0
    }
    return v
  }, [geo, dims])

  const pronto = useMemo(() => {
    if (!geo) return false
    for (const k of geo.entradas) {
      if (INT_FIELDS.has(k)) continue
      if (!dims[k] || Number(dims[k]) <= 0) return false
    }
    return true
  }, [geo, dims])

  const resultado = useMemo(() => {
    if (!pronto) return null
    return calcularCusto(modelo, vars, tipoCor, perda)
  }, [pronto, modelo, vars, tipoCor, perda])

  const multi = useMemo(() => {
    const p = getPrecos()
    return Number(p.multiplicadores?.[modelo]) || Number(p.multiplicadorDefault) || 3.5
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelo, versaoPrecos])

  const adicionaisTotal = useMemo(() => {
    let total = 0
    if (adicionais.puxadorTipo)
      total += (PRECO_PUXADOR[adicionais.puxadorTipo] || 0) * (Number(adicionais.puxadorQtd) || 1)
    ADICIONAIS_BOOLEAN.forEach(({ key }) => {
      if (adicionais[key]) total += PRECO_ADICIONAL[key] || 0
    })
    ADICIONAIS_QUANTIDADE.forEach(({ key }) => {
      const qtd = Number(adicionais[key] || 0)
      if (qtd > 0) total += (PRECO_ADICIONAL[key] || 0) * qtd
    })
    return total
  }, [adicionais])

  const baseSem25       = resultado ? resultado.custoBase * multi : 0
  const extra25mm       = adicionais.espessura25mm ? baseSem25 * 0.25 : 0
  const basePreco       = baseSem25 + extra25mm
  // Extras fixos do modelo (ex.: Mesa Industrial soma R$ 500 pelo metalon
  // terceirizado, que não entra no cálculo de MDF×multiplicador).
  const extraFixoModelo = Number(geo?.extraFixo) || 0
  const precoFinal      = basePreco + adicionaisTotal + extraFixoModelo
  const precoArredondado = arredondarPreco(precoFinal)

  // Campos de medida do modelo selecionado
  const dimFields = geo ? [
    geo.entradas.includes('C')        && { k: 'C',        label: 'Comp.',       tipo: 'num' },
    geo.entradas.includes('C1')       && { k: 'C1',       label: 'Braço +',     tipo: 'num' },
    geo.entradas.includes('C2')       && { k: 'C2',       label: 'Braço −',     tipo: 'num' },
    geo.entradas.includes('P')        && { k: 'P',        label: 'Prof.',       tipo: 'num' },
    geo.entradas.includes('A')        && { k: 'A',        label: 'Altura',      tipo: 'num' },
    geo.entradas.includes('n_g')      && { k: 'n_g',      label: 'Gavetas',     tipo: 'int' },
    geo.entradas.includes('n_prat')   && { k: 'n_prat',   label: 'Prateleiras', tipo: 'int' },
    geo.entradas.includes('n_div')    && { k: 'n_div',    label: 'Divisórias',  tipo: 'int' },
    geo.entradas.includes('n_portas') && { k: 'n_portas', label: 'Portas',      tipo: 'int' },
  ].filter(Boolean) : []

  return (
    <div className="max-w-5xl mx-auto px-4 py-2">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-base font-bold text-slate-800 flex items-center gap-2 shrink-0">
          <Wrench size={17} className="text-[#CC8800]" />
          Precificação
        </h1>
        <button
          onClick={() => setOrcamentoOpen(true)}
          className="mx-auto flex items-center gap-2 px-5 py-1.5 bg-[#F5C200] hover:bg-[#e6b800] text-[#111111] font-bold text-sm rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95"
        >
          <Calculator size={15} />
          Orçamento personalizado
        </button>
        <button
          onClick={atualizarManual}
          disabled={atualizando}
          title="Re-sincroniza preços do Supabase. Útil se a designer/admin acabou de mudar um multiplicador e o app não atualizou ainda."
          className="btn-secondary text-xs py-1.5 px-3 shrink-0 disabled:opacity-50"
        >
          <RefreshCw size={12} className={atualizando ? 'animate-spin' : ''} />
          {atualizando ? 'Atualizando…' : 'Atualizar dados'}
        </button>
        <button onClick={resetar} className="btn-secondary text-xs py-1.5 px-3 shrink-0">
          <RotateCcw size={12} />
          Limpar
        </button>
      </div>

      {/* Modal orçamento */}
      <OrcamentoPersonalizado open={orcamentoOpen} onClose={() => setOrcamentoOpen(false)} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_270px] gap-3 items-start">

        {/* ── Coluna esquerda ──────────────────────────────────────────── */}
        <div className="space-y-2">

          {/* Modelo */}
          <div className="card p-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Modelo</label>
            <ModeloCombobox
              value={modelo}
              onChange={handleModelo}
              compact
            />
          </div>

          {/* Cor */}
          <div className="card p-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cor</label>
              <div className="flex gap-1">
                {Object.keys(GRUPOS_CORES).map(g => (
                  <button key={g} type="button" onClick={() => setGrupoCor(g)}
                    className={`px-2 py-0.5 rounded text-xs font-semibold transition-colors
                      ${grupoCor === g ? 'bg-[#111111] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {GRUPOS_CORES[grupoCor].map(nomeCor => {
                const c = CORES.find(x => x.nome === nomeCor)
                const ativa = cor === nomeCor
                if (!c) return null
                return (
                  <button key={nomeCor} type="button" onClick={() => setCor(ativa ? '' : nomeCor)}
                    className={`flex flex-col items-center gap-1 p-1.5 rounded-lg border-2 transition-all
                      ${ativa ? 'border-[#111111] shadow scale-105' : 'border-slate-200 hover:border-slate-400'}`}>
                    <div className="w-8 h-8 rounded-md border border-black/10 relative flex items-center justify-center"
                      style={{ backgroundColor: c.hex }}>
                      {ativa && <Check size={14} className={c.textDark ? 'text-[#111111]' : 'text-white'} strokeWidth={3} />}
                    </div>
                    <span className="text-[9px] text-slate-600 leading-none text-center w-11 font-medium">{nomeCor}</span>
                  </button>
                )
              })}
            </div>

            {corObj && (
              <p className="text-[10px] text-slate-400 mt-1.5">
                Selecionada: <strong className="text-slate-600">{corObj.nome}</strong>
                <span className="ml-1">({corObj.tipo})</span>
              </p>
            )}
          </div>

          {/* Medidas + Puxador */}
          <div className="grid grid-cols-2 gap-2">

            <div className="card p-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Medidas (cm)</label>
              {dimFields.length > 0 ? (
                <div className="space-y-1.5">
                  {dimFields.map(({ k, label, tipo }) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-xs text-slate-400 w-14 shrink-0">{label}</span>
                      <input type="number" min="1" step={tipo === 'int' ? '1' : '0.1'}
                        max={tipo === 'int' ? '10' : undefined}
                        value={dims[k]} onChange={e => setDim(k, e.target.value)}
                        className="input text-sm text-center py-1" placeholder="0" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic">Selecione um modelo</p>
              )}
            </div>

            <div className="card p-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Puxador</label>

              <div className="flex flex-wrap gap-1 mb-2">
                {PUXADOR_TIPOS.map(tipo => {
                  const ativo = adicionais.puxadorTipo === tipo
                  return (
                    <button key={tipo} type="button"
                      onClick={() => {
                        const novoTipo = ativo ? '' : tipo
                        const semCor = ['Cava', 'Fecho e toque', 'Passante'].includes(novoTipo)
                        setAdicionais(p => ({
                          ...p,
                          puxadorTipo: novoTipo,
                          puxadorCor:  semCor ? '' : p.puxadorCor,
                          puxadorQtd:  novoTipo ? (p.puxadorQtd || 1) : 0,
                        }))
                      }}
                      className={`px-2 py-1 rounded text-xs border font-medium transition-all
                        ${ativo ? 'border-[#111111] bg-[#111111] text-white' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}>
                      {tipo}
                    </button>
                  )
                })}
              </div>

              {adicionais.puxadorTipo && (
                <div className="space-y-1.5">
                  {!['Cava', 'Fecho e toque', 'Passante'].includes(adicionais.puxadorTipo) && (
                    <div className="flex flex-wrap gap-1">
                      {PUXADOR_CORES.map(c => {
                        const ativa = adicionais.puxadorCor === c.nome
                        return (
                          <button key={c.nome} type="button"
                            onClick={() => setAd('puxadorCor', ativa ? '' : c.nome)}
                            title={c.nome}
                            className={`w-6 h-6 rounded-full border-2 transition-all
                              ${ativa ? 'border-[#F5C200] scale-110 shadow' : 'border-transparent hover:border-slate-300'}`}
                            style={{ backgroundColor: c.hex }} />
                        )
                      })}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Qtd</span>
                    <Stepper value={adicionais.puxadorQtd || 0} onChange={v => setAd('puxadorQtd', v)} />
                  </div>
                </div>
              )}
              {!adicionais.puxadorTipo && (
                <p className="text-[10px] text-slate-400 italic">Selecione um tipo</p>
              )}
            </div>

          </div>

          {/* Adicionais */}
          <div className="card p-2">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Adicionais</label>

            <div className="grid grid-cols-4 gap-1 mb-1.5">
              {ADICIONAIS_BOOLEAN.map(({ key, label }) => (
                <label key={key}
                  className={`flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer transition-all text-[11px]
                    ${adicionais[key]
                      ? 'bg-[#111111] text-white border-[#111111] font-medium'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  <input type="checkbox" checked={!!adicionais[key]}
                    onChange={e => setAd(key, e.target.checked)}
                    className="accent-[#F5C200] shrink-0 w-3 h-3" />
                  <span className="truncate">{label}</span>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-1">
              {ADICIONAIS_QUANTIDADE.map(({ key, label }) => (
                <div key={key}
                  className={`flex items-center justify-between gap-1 rounded-lg px-2 py-1 border transition-colors
                    ${adicionais[key] > 0 ? 'bg-[#F5C200]/10 border-[#F5C200]/40' : 'bg-white border-slate-200'}`}>
                  <span className="text-[11px] text-slate-700 flex-1 leading-tight truncate">{label}</span>
                  <Stepper value={adicionais[key] || 0} onChange={v => setAd(key, v)} />
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ── Painel de resultado ─────────────────────────────────────── */}
        <div className="sticky top-20">
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wrench size={14} className="text-[#CC8800]" />
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Resumo</h3>
            </div>

            {!resultado ? (
              <div className="py-10 text-center text-slate-300">
                <Wrench size={28} className="mx-auto mb-2 opacity-30" />
                <p className="text-xs">{!modelo ? 'Selecione um modelo' : 'Preencha as medidas'}</p>
              </div>
            ) : (
              <>
                <div className="space-y-1.5 mb-3">
                  {/* Modelo + cor */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-800 leading-snug truncate">{modelo}</p>
                      {corObj && (
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-black/10"
                            style={{ backgroundColor: corObj.hex }} />
                          {corObj.nome}
                        </p>
                      )}
                    </div>
                    <span className="text-xs font-semibold text-slate-700 shrink-0">{fmt(baseSem25)}</span>
                  </div>

                  {/* Adicionais detalhados */}
                  {adicionais.puxadorTipo && (
                    <div className="flex justify-between gap-2">
                      <span className="text-xs text-slate-500 flex-1 truncate">
                        Puxador {adicionais.puxadorTipo}{adicionais.puxadorCor ? ` ${adicionais.puxadorCor}` : ''} ×{adicionais.puxadorQtd || 1}
                      </span>
                      <span className="text-xs font-semibold text-slate-600 shrink-0">
                        {fmt((PRECO_PUXADOR[adicionais.puxadorTipo] || 0) * (adicionais.puxadorQtd || 1))}
                      </span>
                    </div>
                  )}
                  {ADICIONAIS_BOOLEAN.filter(({ key }) => adicionais[key]).map(({ key, label }) => (
                    <div key={key} className="flex justify-between gap-2">
                      <span className="text-xs text-slate-500 flex-1 truncate">{label}</span>
                      <span className="text-xs font-semibold text-slate-600 shrink-0">
                        {fmt(key === 'espessura25mm' ? extra25mm : (PRECO_ADICIONAL[key] || 0))}
                      </span>
                    </div>
                  ))}
                  {ADICIONAIS_QUANTIDADE.filter(({ key }) => adicionais[key] > 0).map(({ key, label }) => (
                    <div key={key} className="flex justify-between gap-2">
                      <span className="text-xs text-slate-500 flex-1 truncate">{label} ×{adicionais[key]}</span>
                      <span className="text-xs font-semibold text-slate-600 shrink-0">
                        {fmt((PRECO_ADICIONAL[key] || 0) * adicionais[key])}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="bg-[#111111] rounded-xl px-3 py-2.5 flex items-center justify-between">
                  <span className="text-xs font-bold text-white">Preço sugerido</span>
                  <span className="text-lg font-black text-[#F5C200]">{fmt(precoArredondado)}</span>
                </div>

                {/* Texto pra WhatsApp + botão Copiar */}
                <TextoWhatsApp
                  modelo={modelo}
                  corObj={corObj}
                  dims={dims}
                  geo={geo}
                  adicionais={adicionais}
                  preco={precoArredondado}
                  copiado={copiado}
                  setCopiado={setCopiado}
                />

              </>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}
